import { ReportingRouterEvent } from '../../../../route/document/base/reportBaseInterface';
import {
    ReportingHandlerConfig,
    ReportingHandlerProps,
    ReportingMessageHandler,
} from './handlerInterface';
import {
    ContextItem,
    ThreadContextItem,
    ThreadContextTaskItemStepsInner,
    ThreadGenerateRequestContextInner,
    ThreadGenerateRequestContextInnerOneOfData,
} from '@varos/assistant-sdk';
import { assertNever } from '@varos/util-typescript';
import { ObjectSelection } from '@varos/editor-core';
import { BlockCreatePayload } from '@varos/research-sdk-api';

export function createAnalyzeSelectionHandler(
    config: ReportingHandlerConfig
): ReportingMessageHandler<'analyze_selection'> {
    const generateMessagesByContentObject: Record<ObjectSelection['kind'], string> = {
        form: "Define the thread title, content type, goal and steps to perform based on the user's form",
        collection:
            "Define the thread title, content type, goal and steps to perform by reading the user's collection, understanding the collection structure and the documents in it",
        content:
            "Define the thread title, content type, goal and steps to perform by reading the user's document, understanding the document structure and the content in it",
    };
    const messagesByContentObject: Record<ObjectSelection['kind'], string> = {
        form: 'Read the attached form from the context and use the task goal and steps to define the plan to perform the task',
        collection:
            'Read the attached collection from the context and use the task goal and steps to define the plan to perform the task',
        content:
            'Read the attached content from the context and use the task goal and steps to define the plan to perform the task',
    };
    const immediateMessagesByContentObject: Record<ObjectSelection['kind'], string> = {
        form: '[no-interview] Read the attached form from the context and use all of the context provided to you in order to start the workflow immediately.',
        collection:
            '[no-interview] Read the attached collection from the context and use all of the context provided to you in order to start the workflow immediately.',
        content:
            '[no-interview] Read the attached content from the context and use all of the context provided to you in order to start the workflow immediately.',
    };
    return async (props: ReportingHandlerProps<'analyze_selection'>) => {
        const { event, mutations, chatState } = props;
        const { selection } = event.data;

        let threadStartContext: ThreadContextItem;

        let contextValue: ThreadGenerateRequestContextInner;
        switch (selection.kind) {
            case 'form':
                contextValue = {
                    kind: 'form',
                    data: {
                        formId: selection.id,
                    },
                };
                threadStartContext = {
                    kind: 'form',
                    formId: selection.id,
                };
                break;
            case 'collection':
                contextValue = {
                    kind: 'collection',
                    data: {
                        collectionId: selection.id,
                    },
                };
                threadStartContext = {
                    kind: 'collection',
                    collectionId: selection.id,
                };
                break;
            case 'content':
                contextValue = {
                    kind: 'root',
                    data: {
                        document: selection.id,
                    },
                };
                threadStartContext = {
                    kind: 'root',
                    document: selection.id,
                };
                break;
        }

        const thread = await mutations.threadCreate.mutateAsync({
            title: 'New Analyze Thread',
            context: [threadStartContext],
        });
        chatState.onChange({
            thread: thread.id,
            isOpen: true,
            assistantMode: 'workspace',
            state: 'preparing',
        });
        // Generate thread suggestion based on the selection
        try {
            const threadSuggestion = await mutations.threadGenerate.mutateAsync({
                prompt: generateMessagesByContentObject[selection.kind],
                mode: 'workspace',
                context: [contextValue],
            });
            const taskCtx = threadSuggestion.context.find(
                (
                    item
                ): item is {
                    kind: 'task';
                    goal: string;
                    steps: ThreadContextTaskItemStepsInner[];
                } => item.kind === 'task'
            );
            const blocks: BlockCreatePayload[] = [
                {
                    kind: 'heading',
                    level: 1,
                    items: [
                        {
                            kind: 'text',
                            text: threadSuggestion.title,
                        },
                    ],
                },
            ];
            if (taskCtx) {
                blocks.push({
                    kind: 'text',
                    items: [
                        {
                            kind: 'text',
                            text: taskCtx.goal,
                        },
                    ],
                });
                blocks.push(
                    ...taskCtx.steps.map((step): BlockCreatePayload => {
                        return {
                            kind: 'list_item',
                            style: 'bullet',
                            items: [
                                {
                                    kind: 'text',
                                    text: `${step.icon} ${step.display_name} - ${step.description}`,
                                },
                            ],
                        };
                    })
                );
            }

            const rootDocument = await mutations.documentCreate.mutateAsync({
                title: threadSuggestion.title,
                type: 'document',
                icon: threadSuggestion.emoji,
                blocks,
            });
            config.pubsub.dispatch({
                kind: 'open',
                data: {
                    content: {
                        id: rootDocument.id,
                    },
                },
            });

            console.log('thread suggestion', threadSuggestion);

            console.log('updating thread');

            await mutations.threadUpdate.mutateAsync([
                thread.id,
                {
                    title: threadSuggestion.title,
                    context: [
                        ...threadSuggestion.context,
                        {
                            kind: 'root',
                            document: rootDocument.id,
                        },
                    ],
                },
            ]);
            console.log('thread updated', thread);

            // Create the message
            console.log('creating message');

            chatState.onChange({
                thread: thread.id,
                isOpen: true,
                assistantMode: 'workspace',
                state: 'active',
                enqueuedMessages: [
                    {
                        idempotencyKey: new Date().getTime().toString(),
                        parts: [
                            {
                                type: 'text',
                                text: event.data.start_immediately
                                    ? immediateMessagesByContentObject[selection.kind]
                                    : messagesByContentObject[selection.kind],
                            },
                        ],
                        context: [
                            {
                                kind: 'root',
                                data: {
                                    document: rootDocument.id,
                                },
                            },
                            ...threadSuggestion.context.map((item): ContextItem => {
                                if (item.kind === 'prompt') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            text: item.prompt,
                                        },
                                    };
                                }
                                if (item.kind === 'root' || item.kind === 'reference') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            document: item.document,
                                        },
                                    };
                                }
                                if (item.kind === 'task') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            goal: item.goal,
                                            steps: item.steps,
                                        },
                                    };
                                }
                                if (item.kind === 'form') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            formId: item.formId,
                                        },
                                    };
                                }
                                if (item.kind === 'template') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            template: item.template,
                                        },
                                    };
                                }
                                if (item.kind === 'collection') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            collectionId: item.collectionId,
                                        },
                                    };
                                }
                                if (item.kind === 'file') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            filename: item.filename,
                                            type: item.type,
                                            content: item.content,
                                        },
                                    };
                                }
                                if (item.kind === 'resource') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            resource_id: item.resource_id,
                                            namespace: item.namespace,
                                        },
                                    };
                                }
                                if (item.kind === 'selection') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            document: item.document,
                                            blocks: item.blocks,
                                            start: item.start,
                                            end: item.end,
                                            plaintext: item.plaintext,
                                        },
                                    };
                                }
                                if (item.kind === 'solution') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            solutionId: item.solutionId,
                                        },
                                    };
                                }
                                if (item.kind === 'integration') {
                                    return {
                                        kind: item.kind,
                                        data: {
                                            integrationId: item.integrationId,
                                        },
                                    };
                                }
                                assertNever(item);
                            }),
                        ],
                    },
                ],
            });

            // config.pubsub.dispatch({
            //     kind: 'send_to_chat',
            //     data: {
            //         parts: [
            //             {
            //                 type: 'text',
            //                 text: messagesByContentObject[selection.kind],
            //             },
            //         ],
            //         context: threadSuggestion.context.map((item): ContextItem => {
            //             if (item.kind === 'prompt') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         text: item.prompt,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'root' || item.kind === 'reference') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         document: item.document,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'task') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         goal: item.goal,
            //                         steps: item.steps,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'form') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         formId: item.formId,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'template') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         template: item.template,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'collection') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         collectionId: item.collectionId,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'file') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         filename: item.filename,
            //                         type: item.type,
            //                         content: item.content,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'resource') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         resource_id: item.resource_id,
            //                         namespace: item.namespace,
            //                     },
            //                 };
            //             }
            //             if (item.kind === 'selection') {
            //                 return {
            //                     kind: item.kind,
            //                     data: {
            //                         document: item.document,
            //                         blocks: item.blocks,
            //                         start: item.start,
            //                         end: item.end,
            //                         plaintext: item.plaintext,
            //                     },
            //                 };
            //             }
            //             assertNever(item);
            //         }),
            //     },
            // });
        } catch (error) {
            console.error('error preparing thread', error);
            chatState.onChange({
                thread: thread.id,
                isOpen: true,
                assistantMode: 'workspace',
                state: 'active',
                enqueuedMessages: null,
            });
            throw error;
        }
    };
}
